import {faCaretDown, faCaretUp} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Tooltip} from 'antd';
import dayjs from 'dayjs';
import {useMemo} from 'react';

import {
    danger60,
    gray5,
    green60,
    lps_semi_circle_progress_high_rating_color,
    lps_semi_circle_progress_low_rating_color,
    lps_semi_circle_progress_medium_rating_color,
} from '../../css/var-export.scss';
import {LocalePlural} from '../../provider/locale/locale-plural';
import {useLpsAverageRating} from '../../service/dashboard/dashboard-lps/dashboard-lps-hook';
import {useFormat} from '../../util/format-hook/format-hook';
import {Text} from '../text/text';

import * as styles from './semi-circle-progress.scss';

export function SemiCircleProgress(): JSX.Element {
    const {startDate, endDate} = useMemo(
        () => ({
            startDate: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
        }),
        []
    );

    const {getFormattedNumber} = useFormat();

    const {data} = useLpsAverageRating({startDate, endDate});

    const {clampedValue, delta, dotX, dotY} = useMemo(() => {
        const memoizedClampedValue = Math.max(0, Math.min(100, data?.[1]?.avgLps || 0));
        const memoizedDelta = (data?.[1]?.avgLps || 0) - (data?.[0]?.avgLps || 0);
        const angle = (memoizedClampedValue / 100) * 180;
        const dotAngle = ((180 - angle) * Math.PI) / 180;
        const memoizedDotX = 60 + 50 * Math.cos(dotAngle);
        const memoizedDotY = 60 - 50 * Math.sin(dotAngle);

        return {
            clampedValue: memoizedClampedValue,
            delta: memoizedDelta,
            angle,
            dotX: memoizedDotX,
            dotY: memoizedDotY,
        };
    }, [data]);

    const getColor = useMemo(() => {
        if (clampedValue < 50) {
            return lps_semi_circle_progress_low_rating_color;
        }

        if (clampedValue < 80) {
            return lps_semi_circle_progress_medium_rating_color;
        }

        return lps_semi_circle_progress_high_rating_color;
    }, [clampedValue]);

    return (
        <div className={styles.SemiCircleProgress}>
            <div className={styles.SemiCircleProgress_svgContainer}>
                <svg height="180" viewBox="0 0 130 70" width="300">
                    <path
                        d="M 10 60 A 50 50 0 0 1 57 10"
                        fill="none"
                        stroke={lps_semi_circle_progress_low_rating_color}
                        strokeLinecap="round"
                        strokeWidth="5"
                    />

                    <path
                        d="M 63 10 A 50 50 0 0 1 99 29"
                        fill="none"
                        stroke={lps_semi_circle_progress_medium_rating_color}
                        strokeLinecap="round"
                        strokeWidth="5"
                    />

                    <path
                        d="M 110 60 A 50 50 0 0 0 103 34"
                        fill="none"
                        stroke={lps_semi_circle_progress_high_rating_color}
                        strokeLinecap="round"
                        strokeWidth="5"
                    />

                    <line stroke={gray5} strokeWidth="0.5" x1="15" x2="20" y1="61" y2="61" />
                    <line stroke={gray5} strokeWidth="0.5" x1="28" x2="30" y1="28" y2="30" />
                    <line stroke={gray5} strokeWidth="0.5" x1="60" x2="60" y1="14" y2="19" />
                    <line stroke={gray5} strokeWidth="0.5" x1="90" x2="92" y1="30" y2="28" />
                    <line stroke={gray5} strokeWidth="0.5" x1="100" x2="105" y1="61" y2="61" />

                    <circle
                        className={styles.SemiCircleProgress_dot}
                        cx={dotX}
                        cy={dotY}
                        fill={getColor}
                        r="4"
                        stroke="white"
                        strokeWidth="4"
                    />

                    <text className={styles.SemiCircleProgress_label} textAnchor="middle" x="2" y="62">
                        0
                    </text>
                    <text className={styles.SemiCircleProgress_label} textAnchor="middle" x="60" y="5">
                        50
                    </text>
                    <text className={styles.SemiCircleProgress_label} textAnchor="middle" x="110" y="30">
                        80
                    </text>
                    <text className={styles.SemiCircleProgress_label} textAnchor="middle" x="122" y="62">
                        100
                    </text>
                </svg>
                <div className={styles.SemiCircleProgress_value}>
                    <div>
                        {Math.round(clampedValue)}
                        <Tooltip
                            title={
                                delta >= 0 ? (
                                    <LocalePlural
                                        count={delta}
                                        fewKey="DASHBOARD_PAGE__LPS_DELTA__TOOLTIP_FEW"
                                        manyKey="DASHBOARD_PAGE__LPS_DELTA__TOOLTIP_MANY"
                                        singularKey="DASHBOARD_PAGE__LPS_DELTA__TOOLTIP_SINGULAR"
                                        valueMap={{
                                            count: getFormattedNumber(delta, {maximumFractionDigits: 2}),
                                            date: dayjs().subtract(2, 'day').format('DD.MM.YYYY'),
                                        }}
                                    />
                                ) : (
                                    <LocalePlural
                                        count={delta}
                                        fewKey="DASHBOARD_PAGE__LPS_DELTA__TOOLTIP_NEGATIVE_FEW"
                                        manyKey="DASHBOARD_PAGE__LPS_DELTA__TOOLTIP_NEGATIVE_MANY"
                                        singularKey="DASHBOARD_PAGE__LPS_DELTA__TOOLTIP_NEGATIVE_SINGULAR"
                                        valueMap={{
                                            count: getFormattedNumber(delta * -1, {maximumFractionDigits: 2}),
                                            date: dayjs().subtract(2, 'day').format('DD.MM.YYYY'),
                                        }}
                                    />
                                )
                            }
                        >
                            <div>
                                <Text
                                    bolder
                                    className={styles.SemiCircleProgress_delta}
                                    style={{color: delta >= 0 ? green60 : danger60}}
                                >
                                    <FontAwesomeIcon icon={delta >= 0 ? faCaretUp : faCaretDown} />{' '}
                                    {Math.abs(delta).toFixed(0)}
                                </Text>
                            </div>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </div>
    );
}
